import { useEffect, type ReactNode } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
}

/** Bottom sheet with fade + slide-up animation. */
export function Sheet({ open, onClose, title, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1150,
        display: "flex",
        alignItems: "flex-end",
        animation: "sp-fade-in var(--d-med) var(--ease-out)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 640,
          margin: "0 auto",
          background: "var(--bg)",
          borderTopLeftRadius: "var(--r-xl)",
          borderTopRightRadius: "var(--r-xl)",
          paddingBottom: "max(16px, var(--safe-bottom))",
          maxHeight: "90dvh",
          overflowY: "auto",
          animation: "sp-slide-up var(--d-slow) var(--ease-out)",
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "center",
          paddingTop: 8,
        }}>
          <div style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: "var(--separator)",
          }} />
        </div>
        {title && (
          <div style={{ padding: "12px 16px 0" }}>
            <h2 className="h2" style={{ margin: 0 }}>{title}</h2>
          </div>
        )}
        <div style={{ padding: "16px 0" }}>{children}</div>
      </div>
    </div>
  );
}
